import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useMemo } from 'react';
import type { Person, Relationship, Source, GenealogyEvent } from '../types';
import { isEigenDeelname } from '../../shared/eigenGebeurtenis.mjs';

interface DataState {
  // State
  people: Person[];
  relationships: Relationship[];
  sources: Source[];
  events: GenealogyEvent[];
  eventsByPerson: Map<string, string[]>;
  loadedEventProjectId: string | null;
  /** #1825 — van welk project `people` is opgehaald; `null` = (nog) niets geladen. Spiegelt `loadedEventProjectId`. */
  peopleProjectId: string | null;
  personStoryCount: Map<string, number>;
  reviewCount: number;

  // Bulk setters
  /** `projectId` alleen meegeven bij een volledige (her)laad van een project; `null` wist de herkomst. */
  setPeople: (people: Person[], projectId?: string | null) => void;
  setRelationships: (relationships: Relationship[]) => void;
  setSources: (sources: Source[]) => void;
  setEvents: (events: GenealogyEvent[], projectId: string) => void;
  setPersonStoryCount: (map: Map<string, number>) => void;
  setReviewCount: (count: number) => void;

  // People thin actions
  updatePerson: (id: string, patch: Partial<Person>) => void;
  applyPersonSnapshot: (person: Person) => void;
  addPerson: (person: Person) => void;
  removePerson: (id: string) => void;

  // Events thin actions (update cache na succesvolle API-call)
  updateEvent: (id: string, patch: Partial<GenealogyEvent>) => void;
  addEvent: (event: GenealogyEvent) => void;
  removeEvent: (id: string) => void;

  // Sources thin actions
  updateSource: (id: string, patch: Partial<Source>) => void;
  addSource: (source: Source) => void;
  removeSource: (id: string) => void;
}

function buildEventsByPerson(events: GenealogyEvent[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const ev of events) {
    const participants = ev.participants || [];
    for (const p of participants) {
      const pid = p.personId || (p as { person_id?: string }).person_id;
      // #1947 — dezelfde regel als de server: alleen eigen gebeurtenissen, niet waar iemand vermeld staat.
      if (!pid || !isEigenDeelname(ev.type, p.role)) continue;
      const list = map.get(pid);
      if (list) list.push(ev.id);
      else map.set(pid, [ev.id]);
    }
  }
  return map;
}

const deelnemerIds = (ev: GenealogyEvent): string[] =>
  (ev.participants || [])
    .filter((p) => isEigenDeelname(ev.type, p.role))
    .map((p) => p.personId || (p as { person_id?: string }).person_id)
    .filter((pid): pid is string => !!pid);

/**
 * #800 / #835 — de gebeurtenissen staan twee keer in het geheugen: in de
 * `events`-store en ingebed in `person.events` (daar leest de rapportbouwer).
 * Deze helper houdt de ingebedde kopie gelijk na een event-mutatie: deelnemers
 * krijgen de nieuwe versie, wie geen deelnemer (meer) is raakt hem kwijt.
 * `event = null` betekent verwijderd. Ongewijzigde personen houden hun object.
 */
function metEventInPersonen(people: Person[], id: string, event: GenealogyEvent | null): Person[] {
  const deelnemers = new Set(event ? deelnemerIds(event) : []);
  let gewijzigd = false;
  const result = people.map((p) => {
    const lijst = p.events || [];
    const idx = lijst.findIndex((e) => e.id === id);
    if (event && deelnemers.has(p.id)) {
      gewijzigd = true;
      const events = [...lijst];
      if (idx >= 0) events[idx] = event;
      else events.push(event);
      return { ...p, events };
    }
    if (idx < 0) return p;
    gewijzigd = true;
    return { ...p, events: lijst.filter((e) => e.id !== id) };
  });
  return gewijzigd ? result : people;
}

/**
 * De lijst-endpoint levert per ingebed event minder velden dan GET /events
 * (geen bron-citaties, projectId, religie, restrictie, tijdstempels); `eventFromServer`
 * vult die dan met lege waarden. Bij het overnemen in de `events`-store mogen die
 * lege waarden de rijkere versie niet overschrijven.
 */
function lijstVeldenVanEvent(ev: GenealogyEvent): Partial<GenealogyEvent> {
  const {
    projectId: _projectId,
    sources: _sources,
    religion: _religion,
    restriction: _restriction,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...velden
  } = ev;
  return velden;
}

export const useDataStore = create<DataState>((set) => ({
  // Initial state
  people: [],
  relationships: [],
  sources: [],
  events: [],
  eventsByPerson: new Map(),
  loadedEventProjectId: null,
  peopleProjectId: null,
  personStoryCount: new Map(),
  reviewCount: 0,

  // Bulk setters
  setPeople: (people, projectId) =>
    set((s) => ({
      people,
      peopleProjectId: projectId === undefined ? s.peopleProjectId : projectId,
    })),
  setRelationships: (relationships) => set({ relationships }),
  setSources: (sources) => set({ sources }),
  setEvents: (events, projectId) =>
    set({
      events,
      eventsByPerson: buildEventsByPerson(events),
      loadedEventProjectId: projectId,
    }),
  setPersonStoryCount: (personStoryCount) => set({ personStoryCount }),
  setReviewCount: (reviewCount) => set({ reviewCount }),

  // People thin actions
  updatePerson: (id, patch) =>
    set((s) => ({
      people: s.people.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    })),
  /**
   * #800 / #835 — de server stuurt na opslaan de volledige persoon (zelfde vorm
   * als de lijst). Die VERVANGT onze kopie; mergen met een kale rij wiste eerder
   * events en bronnen. Daarnaast volgt de `events`-store: events van deze persoon
   * die niet meer in de snapshot staan (bv. de doop die de server soft-deletete)
   * verdwijnen, de rest krijgt de actuele velden, nieuwe komen erbij.
   */
  applyPersonSnapshot: (person) =>
    set((s) => {
      const bestaat = s.people.some((p) => p.id === person.id);
      const people = bestaat
        ? s.people.map((p) => (p.id === person.id ? person : p))
        : [...s.people, person];

      const snapshot = new Map((person.events || []).map((e) => [e.id, e]));
      const gekoppeld = new Set(s.eventsByPerson.get(person.id) || []);
      const events: GenealogyEvent[] = [];
      for (const ev of s.events) {
        const actueel = snapshot.get(ev.id);
        if (actueel) events.push({ ...ev, ...lijstVeldenVanEvent(actueel) });
        else if (!gekoppeld.has(ev.id)) events.push(ev);
        // gekoppeld maar niet meer in de snapshot: weg op de server
      }
      // Alleen toevoegen als de store dit project bevat; anders laadt setEvents later alles.
      if (s.loadedEventProjectId && s.loadedEventProjectId === person.projectId) {
        const aanwezig = new Set(s.events.map((e) => e.id));
        for (const ev of person.events || []) {
          if (!aanwezig.has(ev.id)) events.push({ ...ev, projectId: person.projectId });
        }
      }
      return { people, events, eventsByPerson: buildEventsByPerson(events) };
    }),
  addPerson: (person) =>
    set((s) => ({
      people: s.people.some((p) => p.id === person.id) ? s.people : [...s.people, person],
    })),
  removePerson: (id) =>
    set((s) => ({
      people: s.people.filter((p) => p.id !== id),
    })),

  // Events thin actions
  // Alle drie houden ook `person.events` bij (metEventInPersonen, #800).
  updateEvent: (id, patch) =>
    set((s) => {
      const events = s.events.map((e) => (e.id === id ? { ...e, ...patch } : e));
      // Staat het event (nog) niet in de store, dan is een volledige patch (met
      // deelnemers, zoals saveEventWithCache hem geeft) genoeg voor person.events.
      const bijgewerkt =
        events.find((e) => e.id === id) ??
        (patch.participants ? ({ ...patch, id } as GenealogyEvent) : undefined);
      return {
        events,
        eventsByPerson: buildEventsByPerson(events),
        people: bijgewerkt ? metEventInPersonen(s.people, id, bijgewerkt) : s.people,
      };
    }),
  addEvent: (event) =>
    set((s) => {
      if (s.events.some((e) => e.id === event.id)) return {};
      const events = [...s.events, event];
      return {
        events,
        eventsByPerson: buildEventsByPerson(events),
        people: metEventInPersonen(s.people, event.id, event),
      };
    }),
  removeEvent: (id) =>
    set((s) => {
      const events = s.events.filter((e) => e.id !== id);
      return {
        events,
        eventsByPerson: buildEventsByPerson(events),
        people: metEventInPersonen(s.people, id, null),
      };
    }),

  // Sources thin actions
  updateSource: (id, patch) =>
    set((s) => ({
      sources: s.sources.map((src) => (src.id === id ? { ...src, ...patch } : src)),
    })),
  addSource: (source) =>
    set((s) => ({
      sources: s.sources.some((src) => src.id === source.id) ? s.sources : [...s.sources, source],
    })),
  removeSource: (id) =>
    set((s) => ({
      sources: s.sources.filter((src) => src.id !== id),
    })),
}));

// === Selector hooks ===
//
// Deze hooks gebruiken `useShallow` om onnodige re-renders te voorkomen
// wanneer niet-relevante events-mutaties plaatsvinden. Ze memo-zen de
// filter-resultaten op basis van de input (personId, type, placeId).

export const useEventsForPerson = (personId: string | null | undefined): GenealogyEvent[] => {
  const { events, eventsByPerson } = useDataStore(
    useShallow((s) => ({ events: s.events, eventsByPerson: s.eventsByPerson }))
  );
  return useMemo(() => {
    if (!personId) return [];
    const ids = eventsByPerson.get(personId);
    if (!ids) return [];
    const idSet = new Set(ids);
    return events.filter((ev) => idSet.has(ev.id));
  }, [personId, events, eventsByPerson]);
};

/**
 * #1947 (V2) — de akten van anderen waar deze persoon op vermeld staat: vader en moeder bij een
 * geboorte, de weduwe bij een overlijden, een getuige bij een huwelijk. Niet zijn eigen
 * gebeurtenissen (die staan in `useEventsForPerson`), wel zichtbaar onder "Vermeld op akten van anderen".
 */
export const useVermeldeEvents = (personId: string | null | undefined): GenealogyEvent[] => {
  const events = useDataStore((s) => s.events);
  return useMemo(() => {
    if (!personId) return [];
    return events.filter((ev) =>
      (ev.participants || []).some(
        (p) =>
          (p.personId || (p as { person_id?: string }).person_id) === personId &&
          !isEigenDeelname(ev.type, p.role)
      )
    );
  }, [personId, events]);
};

export const useEventsByType = (type: string | null | undefined): GenealogyEvent[] => {
  const events = useDataStore((s) => s.events);
  return useMemo(() => {
    if (!type) return [];
    return events.filter((ev) => ev.type === type);
  }, [type, events]);
};

export const useEventsForPlace = (placeId: string | null | undefined): GenealogyEvent[] => {
  const events = useDataStore((s) => s.events);
  return useMemo(() => {
    if (!placeId) return [];
    return events.filter((ev) => ev.placeId === placeId);
  }, [placeId, events]);
};

export const useAllEvents = (): GenealogyEvent[] => useDataStore((s) => s.events);
